/**
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableParams:
 *      type: object
 *      properties:
 *        ChampionshipId:
 *          description: ID of the associated Championship
 *          type: string
 *        Public:
 *          description: Public of the ChampionshipResultTable
 *          type: boolean
 *        Name:
 *          description: Name of the ChampionshipResultTable
 *          type: string
 *        Discards:
 *          description: Discards of the ChampionshipResultTable
 *          type: integer
 *        InvertPoints:
 *          description: InvertPoints of the ChampionshipResultTable
 *          type: boolean
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableParams'
 *        - type: object
 *          required:
 *            - ChampionshipId
 *            - Public
 *            - Name
 *            - Discards
 *            - InvertPoints
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the ChampionshipResultTable
 *              type: string
 *        - $ref: '#/definitions/ChampionshipResultTableParams'
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTable:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableCreateParams'
 *        - $ref: '#/definitions/ChampionshipResultTableUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      ChampionshipResultTable:
 *        allOf:
 *          - $ref: '#/definitions/ChampionshipResultTable'
 *          - type: object
 *            properties:
 *              Championship:
 *                description: Championship of the ChampionshipResultTable
 *                $ref: '#/components/schemas/Championship'
 *              ChampionshipResultTableEntries:
 *                description: ChampionshipResultTableEntries of the ChampionshipResultTable
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipResultTableEntry'
 *              ChampionshipResultTableRaces:
 *                description: ChampionshipResultTableRaces of the ChampionshipResultTable
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipResultTableRace'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  ChampionshipId: string;
};

export type $Params = {
  Discards: number;
  InvertPoints: boolean;
  Name: string;
  Public: boolean;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Championship?: $Model<'Championship'>;
  ChampionshipResultTableEntries?: Array<$Model<'ChampionshipResultTableEntry'>>;
  ChampionshipResultTableRaces?: Array<$Model<'ChampionshipResultTableRace'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Discards: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  InvertPoints: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'ChampionshipResultTable'> => {
  const ChampionshipResultTable = stack.define(
    'ChampionshipResultTable',
    fields(Sequelize),
    {
      tableName: 'ChampionshipResultTables',
    },
  );

  ChampionshipResultTable.associate = (models: $ModelClasses) => {
    models.ChampionshipResultTable.belongsTo(
      models.Championship,
      {
        as: 'Championship',
        foreignKey: 'ChampionshipId',
      },
    );
    models.ChampionshipResultTable.hasMany(
      models.ChampionshipResultTableEntry,
      {
        as: 'ChampionshipResultTableEntries',
        foreignKey: 'ChampionshipResultTableId',
      },
    );
    models.ChampionshipResultTable.hasMany(
      models.ChampionshipResultTableRace,
      {
        as: 'ChampionshipResultTableRaces',
        foreignKey: 'ChampionshipResultTableId',
      },
    );
  };

  ChampionshipResultTable.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return ChampionshipResultTable;
});
